/**
 * Belgian energy add-ons (docs/decisions/0015):
 * - the Flemish capacity tariff: the yearly cost of the average monthly peak (highest quarter-hour
 *   of each month, at least 2.5 kW), before and after adding the project's big loads (EV charger,
 *   heat pump…), and with load management (the charger slows down while the others run);
 * - the grid operator for an address (Fluvius, Sibelga, ORES, RESA) and what it needs to hear
 *   about a PV installation, a battery, a charger or a heat pump.
 * These are estimates for a discussion with the client, not the bill: the real peak is in the
 * digital meter's data.
 */
import type { Project } from '@elec/domain';
import { analyse, type Analysis } from '@elec/analysis';

/** Minimum billed peak of the Flemish capacity tariff (kW). */
export const CAPACITY_MIN_KW = 2.5;

/**
 * Capacity tariff 2026 per Fluvius network area, € per kW per year incl. VAT
 * (source: callmepower.be/nl/energie/gids/tarief/capaciteitstarief, Nov 2026; the regulator
 * VREG sets them yearly — check the client's bill).
 */
export const CAPACITY_TARIFFS_2026: ReadonlyArray<{ area: string; eurPerKwYear: number }> = [
  { area: 'Fluvius Limburg', eurPerKwYear: 51.99 },
  { area: 'Fluvius Antwerpen', eurPerKwYear: 52.37 },
  { area: 'Fluvius Midden-Vlaanderen', eurPerKwYear: 53.13 },
  { area: 'Fluvius Imewo', eurPerKwYear: 57.45 },
  { area: 'Fluvius Halle-Vilvoorde', eurPerKwYear: 59.41 },
  { area: 'Fluvius Zenne-Dijle', eurPerKwYear: 59.49 },
  { area: 'Fluvius Kempen', eurPerKwYear: 59.58 },
  { area: 'Fluvius West', eurPerKwYear: 60.53 },
];

export type LoadKind = 'ev' | 'heat-pump' | 'other';

export interface PeakLoad {
  name: string;
  kind: LoadKind;
  kw: number;
  /** Runs at the time of the household's peak (evening, winter). */
  coincident: boolean;
}

const KIND: Record<string, LoadKind> = { 'iec.ev-charger': 'ev', 'iec.heat-pump': 'heat-pump' };
/** Loads worth weighing for the peak: the big fixed appliances with a stated power. */
const BIG = new Set([
  'iec.ev-charger',
  'iec.heat-pump',
  'iec.cooker',
  'iec.oven',
  'iec.water-heater',
  'iec.heater',
  'iec.dryer',
  'iec.washing-machine',
  'iec.dishwasher',
  'iec.appliance',
]);

/**
 * The project's big loads with a stated power: chargers and heat pumps count at the peak by
 * default (they run in the evening and in winter), the others do not (the base peak includes
 * the household's usual cooking and washing).
 */
export function peakLoads(project: Project, a: Analysis = analyse(project)): PeakLoad[] {
  const out: PeakLoad[] = [];
  for (const c of a.circuits)
    for (const p of c.points) {
      const e = p.element;
      if (!BIG.has(e.symbolId)) continue;
      const kw = Number(e.props.powerKw);
      if (!Number.isFinite(kw) || kw <= 0) continue;
      const kind = KIND[e.symbolId] ?? 'other';
      out.push({
        name: [c.code, c.circuit?.label || e.tag].filter(Boolean).join(' · '),
        kind,
        kw: kw * (typeof e.props.count === 'number' ? e.props.count : 1),
        coincident: kind !== 'other',
      });
    }
  return out;
}

export interface CapacityEstimate {
  /** Average monthly peak before (kW) and its yearly cost (€). */
  before: { peakKw: number; eur: number };
  /** With the loads, unmanaged. */
  after: { peakKw: number; eur: number };
  /** With load management: the chargers at `evMinKw` while the household peaks. */
  managed: { peakKw: number; eur: number; evLimitKw: number } | null;
}

const cost = (kw: number, tariff: number) => Math.round(Math.max(CAPACITY_MIN_KW, kw) * tariff * 100) / 100;
const r1 = (n: number) => Math.round(n * 100) / 100;

/**
 * The capacity tariff before and after. `basePeakKw`: the household's average monthly peak
 * today (from the digital meter; 4 kW is a common figure for a family). Each coincident load adds
 * its power to the peak. Load management keeps chargers at `evMinKw` (6 A: 1.4 kW single-phase,
 * 4.1 kW three-phase) while the household peaks; null when there is no charger to manage.
 */
export function capacityEstimate(
  loads: PeakLoad[],
  opts: { basePeakKw: number; eurPerKwYear: number; evMinKw?: number },
): CapacityEstimate {
  const base = Math.max(0, opts.basePeakKw);
  const on = loads.filter((l) => l.coincident);
  const added = on.reduce((n, l) => n + l.kw, 0);
  const evs = on.filter((l) => l.kind === 'ev');
  const evMin = opts.evMinKw ?? 1.4;
  const managedAdded =
    added - evs.reduce((n, l) => n + l.kw, 0) + evs.reduce((n, l) => n + Math.min(l.kw, evMin), 0);
  return {
    before: { peakKw: r1(base), eur: cost(base, opts.eurPerKwYear) },
    after: { peakKw: r1(base + added), eur: cost(base + added, opts.eurPerKwYear) },
    managed: evs.length
      ? {
          peakKw: r1(base + managedAdded),
          eur: cost(base + managedAdded, opts.eurPerKwYear),
          evLimitKw: evMin,
        }
      : null,
  };
}

export type GridOperator = 'Fluvius' | 'Sibelga' | 'ORES' | 'RESA';

export interface GridOperatorGuess {
  operator: GridOperator;
  region: 'Flanders' | 'Brussels' | 'Wallonia';
  /** Wallonia: ORES or RESA depends on the municipality; RESA covers most of the Liège province. */
  toCheck: boolean;
}

/** The first Belgian postcode (4 digits) in an address. */
export const postcodeOf = (address: string): number | undefined => {
  const m = /\b([1-9]\d{3})\b/.exec(address);
  return m ? Number(m[1]) : undefined;
};

/**
 * The grid operator of a Belgian address from its postcode: Brussels 1000–1299 Sibelga;
 * Flanders 1500–3999 and 8000–9999 Fluvius; Wallonia 1300–1499 and 4000–7999 ORES, or RESA for
 * Liège (4000–4999; to check, some municipalities there are ORES).
 */
export function gridOperatorFor(address: string): GridOperatorGuess | undefined {
  const pc = postcodeOf(address);
  if (pc === undefined) return undefined;
  if (pc >= 1000 && pc <= 1299) return { operator: 'Sibelga', region: 'Brussels', toCheck: false };
  if ((pc >= 1500 && pc <= 3999) || pc >= 8000)
    return { operator: 'Fluvius', region: 'Flanders', toCheck: false };
  if (pc >= 4000 && pc <= 4999) return { operator: 'RESA', region: 'Wallonia', toCheck: true };
  return { operator: 'ORES', region: 'Wallonia', toCheck: pc >= 1300 && pc <= 1499 ? false : true };
}

export interface GridNotice {
  pvKwp: number;
  /** AC power of the inverters (kVA, from their `powerKw`). */
  inverterKva: number;
  evKw: number;
  heatPumpKw: number;
  /** Storage batteries (kWh), also to declare. */
  batteryKwh: number;
  /** Anything to tell the grid operator about. */
  any: boolean;
}

/** What the project has that the grid operator must be told about (PV, inverters, chargers, heat pumps). */
export function gridNotice(project: Project): GridNotice {
  const els = Object.values(project.elements).filter(
    (e) => project.sheets[e.sheetId]?.kind === 'single-line',
  );
  const sum = (id: string, key: string) =>
    els.filter((e) => e.symbolId === id).reduce((n, e) => n + (Number(e.props[key]) || 0), 0);
  // PV strings (decision 0017): modules × power per module.
  const strings = els
    .filter((e) => e.symbolId === 'iec.pv-string')
    .reduce((n, e) => n + ((Number(e.props.modules) || 0) * (Number(e.props.moduleWp) || 0)) / 1000, 0);
  const pvKwp = Math.round((sum('iec.pv-array', 'powerKwp') + strings) * 100) / 100;
  const batteryKwh = sum('iec.battery', 'capacityKwh');
  const inverterKva = sum('iec.inverter', 'powerKw');
  const evKw = sum('iec.ev-charger', 'powerKw');
  const heatPumpKw = sum('iec.heat-pump', 'powerKw');
  const has = (id: string) => els.some((e) => e.symbolId === id);
  return {
    pvKwp,
    inverterKva,
    evKw,
    heatPumpKw,
    batteryKwh,
    any:
      has('iec.pv-array') ||
      has('iec.pv-string') ||
      has('iec.inverter') ||
      has('iec.battery') ||
      has('iec.ev-charger') ||
      has('iec.heat-pump'),
  };
}
